import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  type FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react'
import { setAuthentication } from '@/features/auth/slice'
import type { RootState } from './store'

export const API_BASE = import.meta.env.VITE_API_BASE
export const LEGACY_URL = import.meta.env.VITE_LEGACY_URL

export const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE,
  timeout: 60 * 1000, //milliseconds
  prepareHeaders: async (headers, { getState }) => {
    const token = await awaitToken(getState as () => RootState)
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
      headers.append('X-Network-Source', 'Internal')
    }
    return headers
  },
  responseHandler: async (response) => {
    const contentType = response.headers.get('content-type') ?? ''
    if (
      contentType.includes('application/json') ||
      contentType.includes('application/problem+json')
    ) {
      // Includes application/problem+json (ASP.NET Core ProblemDetails error format).
      return response.json()
    }
    // text/plain, text/html, text/xml, application/xml, or anything else —
    // return raw text to avoid PARSING_ERROR swallowing the real HTTP status code.
    return response.text()
  },
})

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 401)
    api.dispatch(setAuthentication(false))
  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Search',
    'Media',
    'MediaOptions',
    'BulkMedia',
    'BulkFields',
    'HasPartialMedia',
    'Providers',
    'StaleMedia',
  ],
  endpoints: () => ({}),
})

/*
  Awaits a token to be set in state before continuing with iterative polling.
*/
export const awaitToken = async (
  getState: () => RootState,
  attempt = 0,
  limit = 100,
) => {
  for (let i = attempt; i <= limit; i++) {
    const token = getState().reducer.auth.token
    if (token) return token
    await delay(100)
  }
  throw new Error('Took too long to acquire token.')
}

const delay = (delay = 10) =>
  new Promise((resolve) => setTimeout(resolve, delay))
